import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { getDashboardReviews, getDashboardIntegration } from "../services/dashboardApi";
import { SAMPLE_REVIEWS, shouldUseSampleData } from "../data/sampleDashboardData";

const riskClass = (risk) => {
  if (risk >= 80) return "text-danger-main";
  if (risk >= 50) return "text-warning-main";
  return "text-success-main";
};

const formatDate = (value) => {
  if (!value) {
    return {
      date: "Unknown",
      time: "",
    };
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return {
      date: "Unknown",
      time: "",
    };
  }

  return {
    date: date.toLocaleDateString("en-GB"),
    time: date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const formatRisk = (value) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return 0;
  }

  return numeric <= 1
    ? Math.round(numeric * 100)
    : Math.round(numeric);
};

const formatReasons = (reasons) => {
  if (!Array.isArray(reasons)) {
    return [];
  }

  return reasons
    .filter(Boolean)
    .slice(0, 3)
    .map((reason) =>
      String(reason)
        .replace(/[._-]+/g, " ")
        .replace(/\b\w/g, (character) =>
          character.toUpperCase()
        )
    );
};

const getReviewEmail = (review) =>
  review?.signupEmail ||
  review?.signup_email ||
  review?.email ||
  "Unavailable";

const getEvaluationId = (review) =>
  review?.evaluationId ||
  review?.evaluation_id ||
  "";

const getShortEvaluationId = (review) => {
  const evaluationId = getEvaluationId(review);

  if (!evaluationId) {
    return "—";
  }

  return String(evaluationId).slice(0, 8);
};

const getCreatedAt = (review) =>
  review?.evaluationCreatedAt ||
  review?.evaluation_created_at ||
  review?.createdAt ||
  review?.created_at ||
  null;

const getRiskScore = (review) =>
  formatRisk(
    review?.score ??
      review?.finalScore ??
      review?.final_score ??
      review?.riskScore ??
      review?.risk_score
  );

const getReasons = (review) =>
  formatReasons(
    review?.reasonCodes ??
      review?.reason_codes ??
      review?.reasons ??
      []
  );

const isOpenReview = (review) => {
  const status = String(
    review?.status || "open"
  ).toLowerCase();

  return status === "open" || status === "in_review";
};

const ReviewsLayer = () => {
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState("");

  const [lastUpdated, setLastUpdated] = useState(null);

  const loadReviews = useCallback(
    async ({ refresh = false } = {}) => {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setLoadError(null);

      try {
        const integrationResponse = await getDashboardIntegration().catch(() => null);

        const integration = integrationResponse?.integration || null;
        const useSampleData = shouldUseSampleData(integration);

        const response = useSampleData
          ? { reviews: SAMPLE_REVIEWS }
          : await getDashboardReviews({
              limit: 100,
              offset: 0,
              status: "open",
            });

        const rows = Array.isArray(response?.reviews)
          ? response.reviews
          : [];

        setReviews(rows.filter(isOpenReview));
        setLastUpdated(new Date());
      } catch (error) {
        if (
          error?.status === 401 ||
          error?.code === "AUTH_REQUIRED"
        ) {
          navigate("/sign-in", {
            replace: true,
          });
          return;
        }

        setLoadError(error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const openReviews = useMemo(
    () => reviews.filter(isOpenReview),
    [reviews]
  );

  const filteredReviews = useMemo(() => {
    const query = search.trim().toLowerCase();

    return openReviews.filter((review) => {
      const email = getReviewEmail(review).toLowerCase();
      const evaluationId =
        getEvaluationId(review).toLowerCase();
      const shortEvaluationId =
        getShortEvaluationId(review).toLowerCase();

      const reasons = getReasons(review)
        .join(" ")
        .toLowerCase();

      const risk = getRiskScore(review);

      const matchesSearch =
        !query ||
        email.includes(query) ||
        evaluationId.includes(query) ||
        shortEvaluationId.includes(query) ||
        reasons.includes(query);

      let matchesRisk = true;

      if (riskFilter === "low") {
        matchesRisk = risk >= 0 && risk <= 30;
      } else if (riskFilter === "medium") {
        matchesRisk = risk >= 31 && risk <= 70;
      } else if (riskFilter === "high") {
        matchesRisk = risk >= 71 && risk <= 100;
      }

      return matchesSearch && matchesRisk;
    });
  }, [openReviews, search, riskFilter]);

  const refreshReviews = () => {
    if (refreshing || loading) {
      return;
    }

    loadReviews({
      refresh: true,
    });
  };

  return (
    <>
      <div className="row gy-4 mb-24">
        <div className="col-lg-4 col-md-6">
          <div className="card h-100 radius-8 border">
            <div className="card-body p-24">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-secondary-light fw-medium">
                    Open Reviews
                  </span>

                  <h4 className="mb-0 mt-8">
                    {openReviews.length}
                  </h4>
                </div>

                <span className="w-48-px h-48-px rounded-circle bg-warning-focus text-warning-main d-flex align-items-center justify-content-center">
                  <Icon
                    icon="solar:clipboard-check-outline"
                    className="text-2xl"
                  />
                </span>
              </div>

              <p className="text-sm text-secondary-light mt-12 mb-0">
                Signups waiting for a decision
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card radius-8 border reviews-card">
        <div className="card-header border-bottom bg-base py-20 px-24">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h5 className="card-title mb-1">
                Reviews
              </h5>

              <p className="text-secondary-light text-sm mb-0">
                Review signups that require a manual decision
              </p>
            </div>

            <div className="d-flex align-items-center gap-3">
              {lastUpdated && !loading && (
                <span className="text-secondary-light text-xs d-none d-md-inline">
                  Updated{" "}
                  {lastUpdated.toLocaleTimeString(
                    "en-GB",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }
                  )}
                </span>
              )}

              <button
                type="button"
                className="btn btn-outline-primary-600 d-flex align-items-center gap-2"
                onClick={refreshReviews}
                disabled={loading || refreshing}
              >
                <Icon
                  icon="solar:refresh-outline"
                  className={
                    refreshing
                      ? "simplif-refresh-spin"
                      : ""
                  }
                />

                {refreshing
                  ? "Refreshing..."
                  : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        <div className="card-body p-24">
          {!loading &&
            !loadError &&
            openReviews.length > 0 && (
              <div className="row gy-3 mb-20">
                <div className="col-lg-8 col-md-7">
                  <label className="form-label fw-semibold text-primary-light text-sm">
                    Search
                  </label>

                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control radius-8"
                      placeholder="Email or evaluation ID"
                      value={search}
                      onChange={(event) =>
                        setSearch(event.target.value)
                      }
                    />

                    <Icon
                      icon="ion:search-outline"
                      className="position-absolute top-50 translate-middle-y"
                      style={{
                        right: "16px",
                      }}
                    />
                  </div>
                </div>

                <div className="col-lg-4 col-md-5">
                  <label className="form-label fw-semibold text-primary-light text-sm">
                    Risk
                  </label>

                  <select
                    className="form-select radius-8"
                    value={riskFilter}
                    onChange={(event) =>
                      setRiskFilter(
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      All risk levels
                    </option>

                    <option value="low">
                      Low 0–30
                    </option>

                    <option value="medium">
                      Medium 31–70
                    </option>

                    <option value="high">
                      High 71–100
                    </option>
                  </select>
                </div>
              </div>
            )}

          {loading && (
            <div className="py-40 text-center">
              <div
                className="spinner-border text-primary-600"
                role="status"
              >
                <span className="visually-hidden">
                  Loading...
                </span>
              </div>

              <p className="text-secondary-light mt-16 mb-0">
                Loading reviews...
              </p>
            </div>
          )}

          {!loading && loadError && (
            <div className="py-40 text-center">
              <Icon
                icon="solar:danger-triangle-outline"
                width="42"
                className="text-danger-main mb-12"
              />

              <h6 className="mb-8">
                Unable to load reviews
              </h6>

              <p className="text-secondary-light mb-20">
                {loadError?.message ||
                  "The review queue could not be loaded."}
              </p>

              <button
                type="button"
                className="btn btn-primary-600"
                onClick={refreshReviews}
                disabled={refreshing}
              >
                {refreshing
                  ? "Refreshing..."
                  : "Try Again"}
              </button>
            </div>
          )}

          {!loading &&
            !loadError &&
            openReviews.length === 0 && (
              <div className="py-40 text-center">
                <span className="w-64-px h-64-px rounded-circle bg-success-focus text-success-main d-inline-flex align-items-center justify-content-center mb-16">
                  <Icon
                    icon="solar:check-circle-outline"
                    width="32"
                  />
                </span>

                <h6 className="mb-8">
                  Review queue is clear
                </h6>

                <p className="text-secondary-light mb-20">
                  There are no signups waiting for a manual decision.
                </p>

                <button
                  type="button"
                  className="btn btn-outline-primary-600"
                  onClick={refreshReviews}
                  disabled={refreshing}
                >
                  {refreshing
                    ? "Refreshing..."
                    : "Refresh"}
                </button>
              </div>
            )}

          {!loading &&
            !loadError &&
            openReviews.length > 0 &&
            filteredReviews.length === 0 && (
              <div className="py-32 text-center border rounded-8">
                <Icon
                  icon="solar:magnifer-outline"
                  width="34"
                  className="text-secondary-light mb-12"
                />

                <h6 className="mb-6">
                  No matching reviews
                </h6>

                <p className="text-secondary-light text-sm mb-0">
                  Try changing your search or risk filter.
                </p>
              </div>
            )}

          {!loading &&
            !loadError &&
            filteredReviews.length > 0 && (
              <>
                <div className="reviews-table-wrap">
                  <table className="table bordered-table mb-0 reviews-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Evaluation</th>
                        <th>Risk</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredReviews.map(
                        (review) => {
                          const evaluationId =
                            getEvaluationId(
                              review
                            );

                          const shortEvaluationId =
                            getShortEvaluationId(
                              review
                            );

                          const risk =
                            getRiskScore(review);

                          const reasons =
                            getReasons(review);

                          const created =
                            formatDate(
                              getCreatedAt(
                                review
                              )
                            );

                          return (
                            <tr
                              key={
                                review.id ||
                                evaluationId
                              }
                            >
                              <td>
                                <div
                                  className="d-flex align-items-center gap-2"
                                  style={{
                                    minWidth: 0,
                                  }}
                                >
                                  <span className="w-36-px h-36-px rounded-circle bg-warning-focus text-warning-main d-flex align-items-center justify-content-center flex-shrink-0">
                                    <Icon icon="solar:user-rounded-outline" />
                                  </span>

                                  <div
                                    style={{
                                      minWidth: 0,
                                    }}
                                  >
                                    <div
                                      className="fw-medium text-primary-light reviews-email"
                                      title={getReviewEmail(
                                        review
                                      )}
                                    >
                                      {getReviewEmail(
                                        review
                                      )}
                                    </div>

                                    {reasons.length >
                                      0 && (
                                      <div
                                        className="text-secondary-light text-xs reviews-reason"
                                        title={reasons.join(
                                          ", "
                                        )}
                                      >
                                        {reasons.join(
                                          ", "
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td>
                                <span
                                  className="text-secondary-light fw-medium reviews-evaluation-id"
                                  title={
                                    evaluationId
                                  }
                                >
                                  {
                                    shortEvaluationId
                                  }
                                </span>
                              </td>

                              <td>
                                <span
                                  className={`fw-bold ${riskClass(
                                    risk
                                  )}`}
                                >
                                  {risk}
                                </span>

                                <span className="text-secondary-light text-xs">
                                  /100
                                </span>
                              </td>

                              <td>
                                <span className="bg-warning-focus text-warning-main px-10 py-4 rounded-pill fw-semibold text-xs text-nowrap">
                                  In review
                                </span>
                              </td>

                              <td>
                                <div className="d-flex flex-column text-nowrap">
                                  <span className="fw-medium text-primary-light">
                                    {
                                      created.date
                                    }
                                  </span>

                                  <span className="text-secondary-light text-xs">
                                    {
                                      created.time
                                    }
                                  </span>
                                </div>
                              </td>

                              <td>
                                <Link
                                  to={`/reviews/${review.id}`}
                                  className="w-36-px h-36-px bg-primary-50 text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                                  title="Review signup"
                                  aria-label="Review signup"
                                >
                                  <Icon icon="iconamoon:eye-light" />
                                </Link>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-16">
                  <span className="text-secondary-light text-sm">
                    Showing{" "}
                    {filteredReviews.length}{" "}
                    {filteredReviews.length ===
                    1
                      ? "review"
                      : "reviews"}
                  </span>
                </div>
              </>
            )}
        </div>
      </div>

      <style>
        {`
          .reviews-card {
            min-width: 0;
            max-width: 100%;
          }

          .reviews-table-wrap {
            width: 100%;
            max-width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .reviews-table {
            width: 100% !important;
            max-width: 100%;
            table-layout: fixed;
          }

          .reviews-table th,
          .reviews-table td {
            vertical-align: middle;
          }

          .reviews-table th:nth-child(1),
          .reviews-table td:nth-child(1) {
            width: 32%;
          }

          .reviews-table th:nth-child(2),
          .reviews-table td:nth-child(2) {
            width: 14%;
          }

          .reviews-table th:nth-child(3),
          .reviews-table td:nth-child(3) {
            width: 10%;
          }

          .reviews-table th:nth-child(4),
          .reviews-table td:nth-child(4) {
            width: 14%;
          }

          .reviews-table th:nth-child(5),
          .reviews-table td:nth-child(5) {
            width: 20%;
          }

          .reviews-table th:nth-child(6),
          .reviews-table td:nth-child(6) {
            width: 10%;
            text-align: center;
          }

          .reviews-email,
          .reviews-reason {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: block;
          }

          .reviews-evaluation-id {
            white-space: nowrap;
            display: block;
          }

          .simplif-refresh-spin {
            animation: simplifRefreshSpin 0.8s linear infinite;
          }

          @keyframes simplifRefreshSpin {
            from {
              transform: rotate(0deg);
            }

            to {
              transform: rotate(360deg);
            }
          }

          @media (max-width: 991.98px) {
            .reviews-table {
              min-width: 760px;
              table-layout: auto;
            }

            .reviews-table-wrap {
              overflow-x: auto;
            }
          }
        `}
      </style>
    </>
  );
};

export default ReviewsLayer;